import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { 
  Users, Copy, Check, Share2, Gift, Award, Loader2, 
  Twitter, Linkedin, MessageCircle, ChevronRight, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralSection = () => {
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [referralData, setReferralData] = useState({
    referral_code: '',
    referral_link: '',
    total_referrals: 0,
    badges: []
  });
  const [referredUsers, setReferredUsers] = useState([]);

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        axios.get(`${API}/referral/my-code`, { withCredentials: true }),
        axios.get(`${API}/referral/stats`, { withCredentials: true })
      ]);
      
      setReferralData(codeRes.data);
      setReferredUsers(statsRes.data.referred_users || []);
    } catch (error) {
      console.error('Error loading referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent("Rejoignez OpenSapiens, la plateforme de partage de connaissances ! 🚀");
    const url = encodeURIComponent(referralData.referral_link);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent(referralData.referral_link);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`Rejoignez OpenSapiens, la plateforme de partage de connaissances ! 🚀 ${referralData.referral_link}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const getBadgeProgress = () => {
    const count = referralData.total_referrals;
    if (count >= 10) return { current: 'top_ambassadeur', next: null, progress: 100 };
    if (count >= 1) return { current: 'ambassadeur', next: 'top_ambassadeur', progress: (count / 10) * 100, remaining: 10 - count };
    return { current: null, next: 'ambassadeur', progress: 0, remaining: 1 };
  };

  const badgeProgress = getBadgeProgress();

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Referral Card */}
      <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Gift className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Programme de parrainage</h2>
            <p className="text-white/80 text-sm">Invitez vos amis et gagnez des badges</p>
          </div>
        </div>

        {/* Referral Link */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-4 mb-4">
          <label className="text-sm text-white/70 mb-2 block">Votre lien de parrainage</label>
          <div className="flex gap-2">
            <Input
              value={referralData.referral_link}
              readOnly
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50 flex-1"
            />
            <Button
              onClick={copyToClipboard}
              className={`${copied ? 'bg-green-500' : 'bg-white/20'} hover:bg-white/30 min-w-[100px]`}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Copié !
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copier
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={shareOnTwitter}
            className="flex-1 bg-black/20 hover:bg-black/30"
          >
            <Twitter className="h-4 w-4 mr-2" />
            Twitter
          </Button>
          <Button 
            onClick={shareOnLinkedIn}
            className="flex-1 bg-[#0A66C2]/80 hover:bg-[#0A66C2]"
          >
            <Linkedin className="h-4 w-4 mr-2" />
            LinkedIn
          </Button>
          <Button 
            onClick={shareOnWhatsApp}
            className="flex-1 bg-[#25D366]/80 hover:bg-[#25D366]"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            WhatsApp
          </Button>
        </div>
      </div>

      {/* Stats & Progress */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Stats Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-500" />
            Vos statistiques
          </h3>
          
          <div className="text-center py-6">
            <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {referralData.total_referrals}
            </div>
            <p className="text-gray-500 mt-1">
              {referralData.total_referrals === 1 ? 'personne parrainée' : 'personnes parrainées'}
            </p>
          </div>

          {/* Progress to next badge */}
          {badgeProgress.next && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Prochain badge</span>
                <span className="text-purple-600 font-medium">
                  {badgeProgress.next === 'ambassadeur' ? 'Ambassadeur' : 'Top Ambassadeur'}
                </span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
                  style={{ width: `${badgeProgress.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 text-right">
                Plus que {badgeProgress.remaining} parrainage{badgeProgress.remaining > 1 ? 's' : ''} !
              </p>
            </div>
          )}
        </div>

        {/* Badges Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-yellow-500" />
            Vos badges
          </h3>

          <div className="space-y-3">
            {/* Ambassadeur Badge */}
            <div className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
              referralData.total_referrals >= 1 
                ? 'border-blue-200 bg-blue-50' 
                : 'border-gray-100 bg-gray-50 opacity-50'
            }`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                referralData.total_referrals >= 1 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-400'
              }`}>
                <Users className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Ambassadeur</p>
                <p className="text-sm text-gray-500">Parrainer 1 personne</p>
              </div>
              {referralData.total_referrals >= 1 && (
                <Check className="h-6 w-6 text-blue-500" />
              )}
            </div>

            {/* Top Ambassadeur Badge */}
            <div className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
              referralData.total_referrals >= 10 
                ? 'border-yellow-200 bg-yellow-50' 
                : 'border-gray-100 bg-gray-50 opacity-50'
            }`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                referralData.total_referrals >= 10 
                  ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white' 
                  : 'bg-gray-200 text-gray-400'
              }`}>
                <Award className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Top Ambassadeur</p>
                <p className="text-sm text-gray-500">Parrainer 10 personnes</p>
              </div>
              {referralData.total_referrals >= 10 && (
                <Check className="h-6 w-6 text-yellow-500" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Referred Users List */}
      {referredUsers.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Vos filleuls ({referredUsers.length})
          </h3>
          
          <div className="space-y-3">
            {referredUsers.map((user) => (
              <div 
                key={user.user_id}
                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-purple-50 transition-colors"
              >
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user.avatar} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">
                    {user.display_name?.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{user.display_name}</p>
                  <p className="text-xs text-gray-500">
                    Inscrit le {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="bg-gradient-to-r from-cyan-50 to-purple-50 rounded-2xl p-6 border border-purple-100">
        <h3 className="font-semibold text-gray-900 mb-3">💡 Conseils pour parrainer</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
            Partagez votre lien sur vos réseaux sociaux professionnels
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
            Mentionnez OpenSapiens dans vos échanges avec des collègues
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
            Ajoutez votre lien dans votre signature email
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ReferralSection;
